import React, { useEffect, useRef, useState } from 'react';
import { Button, Divider, Input, ModalBody, ModalFooter, ModalHeader, Select, SelectItem, Spinner } from '@nextui-org/react';
import toast from 'react-hot-toast';
import { useSelector } from 'react-redux';
import { SchoolService } from '../../../../services';
import { formatNumericFields, Constants } from '../../../../utils';

const TYPE_QUALIFICATION_OPTIONS = [
  { key: String(Constants.SUBJECTS.TYPE_QUALIFICATION.QUANTITATIVE), label: 'Cuantitativa' },
  { key: String(Constants.SUBJECTS.TYPE_QUALIFICATION.QUALITATIVE), label: 'Cualitativa' },
];

const ModalEditSubject = ({ item, onSuccess, onClose }) => {
  const { form, onChange, onSubmit, schoolYears, loading, filteredGrades } = useForm(item);

  const submit = async () => {
    const isSuccess = await onSubmit();
    if (isSuccess) onSuccess();
  }

  return (
    <>
      <ModalHeader className="flex flex-col gap-1">Editar</ModalHeader>
      <ModalBody className="gap-8 relative">
        {loading ?? (
          <div className="absolute w-full h-full inset-0 z-[70] flex justify-center items-center bg-white/30">
            <Spinner />
          </div>
        )}
        <div className="flex flex-col flex-1 gap-4">
          <Select
            label="Año escolar"
            labelPlacement="outside"
            placeholder=" "
            variant="bordered"
            radius="sm"
            classNames={{ label: 'font-semibold' }}
            isDisabled={!!item}
            disallowEmptySelection={true}
            selectedKeys={!!form.school_year_id ? [form.school_year_id] : []}
            onSelectionChange={v => onChange(v.currentKey, 'school_year_id')}
          >
            {schoolYears.map((item) => (
              <SelectItem key={item.id} value={item.id}>
                {item.name}
              </SelectItem>
            ))}
          </Select>

          <Select
            label="Grado"
            labelPlacement="outside"
            placeholder=" "
            variant="bordered"
            radius="sm"
            classNames={{ label: 'font-semibold' }}

            disallowEmptySelection={true}
            isDisabled={form.school_year_id.length === 0 || !!item}
            selectedKeys={!!form.school_grade_id ? [form.school_grade_id] : []}
            onSelectionChange={v => onChange(v.currentKey, 'school_grade_id')}
          >
            {filteredGrades.map((item) => (
              <SelectItem key={item.id} value={item.id}>
                {item.name}
              </SelectItem>
            ))}
          </Select>

          <Divider />

          <Input
            label="Nombre de la materia"
            labelPlacement="outside"
            placeholder=" "
            radius="sm"
            variant="bordered"
            value={form._subjectName}
            onValueChange={v => onChange(v, '_subjectName')}
          />
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Código de la materia"
              labelPlacement="outside"
              placeholder=" "
              radius="sm"
              variant="bordered"
              value={form._subjectCode}
              onValueChange={v => onChange(v, '_subjectCode')}
            />
            <Input
              label="Horas semanales"
              labelPlacement="outside"
              placeholder=" "
              type="number"
              radius="sm"
              step="1"
              variant="bordered"
              value={form._subjectHours}
              onValueChange={v => onChange(v, '_subjectHours')}
            />
          </div>

          <Select
            label="Tipo de calificación"
            labelPlacement="outside"
            placeholder=" "
            variant="bordered"
            radius="sm"
            classNames={{ label: 'font-semibold' }}
            disallowEmptySelection={true}
            selectedKeys={form._subjectType_qualification ? [String(form._subjectType_qualification)] : []}
            onSelectionChange={(v) => onChange(v.currentKey, '_subjectType_qualification')}
          >
            {TYPE_QUALIFICATION_OPTIONS.map((opt) => (
              <SelectItem key={opt.key} value={opt.key}>
                {opt.label}
              </SelectItem>
            ))}
          </Select>


        </div>
      </ModalBody>
      <ModalFooter>
        <Button variant="light" onPress={onClose}>Cerrar</Button>
        <Button className="bg-green" color="primary" onPress={submit}>Guardar</Button>
      </ModalFooter>
    </>
  )
}

const useForm = (item) => {
  const initialForm = {
    id: null,
    school_year_id: '',
    school_grade_id: '',
    _subjectName: '',
    _subjectCode: '',
    _subjectHours: '',
    _subjectType_qualification: '',
  };
  const user = useSelector((state) => state.user)
  const [form, setForm] = useState(initialForm);
  const [canFetch, setCanFetch] = useState(true);
  const [loading, setLoading] = useState(false);
  const [filteredGrades, setFilteredGrades] = useState([]);
  const [schoolYears, setSchoolYears] = useState([]);
  const grades = useRef([])



  const onChange = (value, target) => {
    if (target === 'school_year_id') {
      setForm(s => ({ ...s, school_grade_id: '' }))
    }

    setForm(s => ({ ...s, [target]: value }));
  }


  const onError = (msg) => {
    toast.error(msg);
    return false;
  }


  const getSchoolYears = async () => {
    if (!canFetch) return;
    setCanFetch(false);
    setLoading(true);
    try {
      const gradesData = await SchoolService.schoolAdmin.getGrades({ school_id: user.id });
      const schoolYears = await SchoolService.schoolAdmin.getSchoolarsYears();
      setSchoolYears(schoolYears);
      grades.current = gradesData;
      setFilteredGrades(gradesData);
      setCanFetch(true);

    } catch (error) {
      setSchoolYears([]);
      onError(String(error));
    } finally {
      setLoading(false);
    }
  }

  const isValidForm = () => {
    if (!form.school_grade_id) {
      onError('Debe seleccionar el grado');
      return false
    }

    if (!form._subjectName || !form._subjectHours || !form._subjectType_qualification) {
      onError('Debe indicar las horas, nombre y tipo de calificación de la materia');
      return false
    }
    return true;
  }

  const onSubmit = async () => {
    if (!isValidForm()) return;
    try {
      const numericFields = ['school_grade_id']
      const formattedData = formatNumericFields(form, numericFields);
      const editData = { 
        ...formattedData, 
        subject_name: form._subjectName, 
        code: form._subjectCode, 
        hours: form._subjectHours, 
        type_qualification: form._subjectType_qualification
      }
      await SchoolService.schoolAdmin.updateSubject(editData)
      toast.success(`Materia editada correctamente`);
      return true;

    } catch (error) {
      onError(String(error));
      return false;
    }
  }

  useEffect(() => {
    getSchoolYears();
  }, [])


  useEffect(() => {
    if (!!item) {
      setForm({
        id: item.id,
        school_year_id: String(item.school_year_id),
        school_grade_id: String(item.school_grade_id),
        _subjectName: item.name,
        _subjectCode: item.code,
        _subjectHours: item.hours,
        _subjectType_qualification: item.type_qualification
      });
    }
  }, [schoolYears])



  return {
    form,
    onChange,
    onSubmit,
    loading,
    schoolYears,
    filteredGrades,
  }
}

export default ModalEditSubject;
